import React from "react";
import { Badge } from "@/components/ui/badge";
import { Calendar, Edit, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CardDescription, CardTitle } from "@/components/ui/card";
import { Class } from "@/types/classTypes";
import { format } from "date-fns";
import ClassStaffAssignment from './ClassStaffAssignment';

interface ClassDetailHeaderProps {
  classData: Class;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
  parentCanSelfEnrollTrial?: boolean;
}

const ClassDetailHeader: React.FC<ClassDetailHeaderProps> = ({
  classData,
  onEdit,
  onDelete,
  isDeleting,
  parentCanSelfEnrollTrial,
}) => {
  const formatEventDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'EEEE, MMMM d, yyyy');
    } catch (e) {
      return dateString;
    }
  };

  const formatEventTime = (timeString?: string) => {
    if (!timeString) return '';
    // Assuming timeString is in HH:MM format
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
        <h2 className="text-xl font-bold">{classData.name}</h2>
        <div className="flex space-x-2">
          {typeof classData.trial_available !== 'undefined' && classData.trial_available && (
            parentCanSelfEnrollTrial === false ? (
              <Badge variant="destructive" title="Parents cannot self-enroll in free trials. Only staff/admins can assign.">
                Free trial: Admin Only
              </Badge>
            ) : (
              <Badge variant="default" title="Parents can self-enroll in free trials.">
                Free trial: Available to parents
              </Badge>
            )
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onEdit}
          >
            <Edit className="h-4 w-4 mr-1" /> Edit
          </Button>
          <Button 
            variant="destructive" 
            size="sm"
            onClick={onDelete}
            disabled={isDeleting}
          >
            <Trash className="h-4 w-4 mr-1" /> Delete
          </Button>
        </div>
      </div>
      <ClassStaffAssignment 
        classId={classData.id}
        organizationId={classData.organization_id}
      />
    </div>
  );
};

export default ClassDetailHeader;
